import { useEffect, useId, useRef, useState, useSyncExternalStore } from "react";
import { api } from "../data";
import { toast } from "./Toast";
import { t as uiT } from "../lib/sprache";
import {
  asThemePreference, currentThemePreference, setThemePreference, themeFromAddress, toggleTheme, type ThemePreference,
} from "../lib/theme";

/**
 * Darstellung je Person (0120, 0121, 0397): Automatisch (Standard, folgt dem Gerät), Hell oder Dunkel.
 *  · Gefragt wird in der Anfangs-Einrichtung (DarstellungAbschnitt): Agentur im ersten Schritt der Einrichtung
 *    (/einrichtung/registrierung, dorthin führt auch die Registrierung), Creator und eingeladene Teammitglieder beim Annehmen
 *    der Einladung. Die Auswahl wirkt sofort als Vorschau; gespeichert wird mit dem Schritt: theme und theme_chosen_at.
 *  · Später im Profil der Agentur-Person und des Creator-Portals (DarstellungWahl) und über den Schalter im Kontomenü; auch
 *    das gilt als gewählt.
 *  · Ohne Wahl (theme_chosen_at leer) gilt der Standard Automatisch bzw. die Wahl dieses Browsers, nicht der Wert in theme.
 *  · Demo: gemerkt wird nur im Browser (localStorage „tk-theme“); die Demo-Datenbank beginnt bei jedem Laden neu.
 */

/** Stand im Konto: die gewählte Darstellung (nur mit theme_chosen_at) und ob die Person gewählt hat (null: unbekannt). */
export interface ThemeAccountState {
  preference: ThemePreference | null;
  chosen: boolean | null;
}

/** Zeile aus user_preferences auswerten: theme gilt erst mit theme_chosen_at als Wahl der Person (0121). */
export function themeStateOf(row: { theme?: string | null; theme_chosen_at?: string | null } | null | undefined): ThemeAccountState {
  if (!row?.theme_chosen_at) return { preference: null, chosen: false };
  return { preference: asThemePreference(row.theme), chosen: true };
}

/**
 * Stand der Person. Demo: nichts aus der Datenbank (die Wahl lebt im Browser). Ohne Zeile: nicht gewählt. Datenbank vor
 * 0121 (Spalte fehlt) oder gerade nicht erreichbar: unbekannt, es bleibt die Wahl des Browsers. Wirft nie.
 */
export async function loadThemeState(userId: string): Promise<ThemeAccountState> {
  if (api.mode === "demo") return { preference: null, chosen: null };
  try {
    const rows = await api.select<{ theme: string | null; theme_chosen_at: string | null }>("user_preferences", {
      columns: "theme, theme_chosen_at", filters: [["user_id", "eq", userId]],
    });
    return themeStateOf(rows[0]);
  } catch {
    return { preference: null, chosen: null };
  }
}

/** Gewählte Darstellung der Person; null, solange sie nicht gewählt hat (oder im Demo-Modus). */
export async function loadThemePreference(userId: string): Promise<ThemePreference | null> {
  return (await loadThemeState(userId)).preference;
}

/**
 * Speichert die Wahl der Person mit Zeitpunkt: theme und theme_chosen_at in der eigenen Zeile (RLS: nur die eigene).
 * Demo: nichts in der Datenbank, die Wahl steht schon im Browser (setThemePreference).
 */
export async function saveThemePreference(userId: string, preference: ThemePreference): Promise<void> {
  if (api.mode === "demo") return;
  const now = new Date().toISOString();
  const own = await api.select<{ user_id: string }>("user_preferences", { columns: "user_id", filters: [["user_id", "eq", userId]] });
  if (own.length) await api.update("user_preferences", [["user_id", "eq", userId]], { theme: preference, theme_chosen_at: now, updated_at: now });
  else await api.insert("user_preferences", { user_id: userId, theme: preference, theme_chosen_at: now }, { columns: "user_id" });
}

/**
 * Wahl aus einem Schritt der Anfangs-Einrichtung übernehmen: wirkt sofort und bleibt im Browser; im Konto mit Zeitpunkt.
 * Wirft nie, damit ein Speicherfehler die Einrichtung nicht aufhält; false heißt: nur auf diesem Gerät übernommen.
 */
export async function saveThemeChoice(userId: string, preference: ThemePreference): Promise<boolean> {
  setThemePreference(preference);
  try {
    await saveThemePreference(userId, preference);
    return true;
  } catch {
    return false;
  }
}

/** Meldung, wenn das Konto die Wahl nicht speichern konnte (sie gilt dann auf diesem Gerät). */
export function themeNotSavedText(t: (de: string, en: string) => string = uiT): string {
  return t("Auf diesem Gerät übernommen. Im Konto ließ sich die Wahl gerade nicht speichern.", "Applied on this device. Your account could not store the choice right now.");
}

/**
 * Nach der Anmeldung gilt die gewählte Darstellung aus dem Konto (auf jedem Gerät) und wird im Browser gemerkt; ohne Wahl
 * bleibt es beim Browser bzw. beim Standard Automatisch. Eine Vorgabe in der Adresse (?theme=) bleibt für diese Seite. Ohne
 * React Query, damit der Rahmen auch ohne Provider zeichnet (Tests).
 */
export function useThemeFromAccount(): void {
  const userId = api.session()?.userId ?? null;
  useEffect(() => {
    if (!userId || themeFromAddress()) return;
    let active = true;
    void loadThemeState(userId).then(({ preference }) => {
      if (active && preference && preference !== currentThemePreference()) setThemePreference(preference);
    });
    return () => {
      active = false;
    };
  }, [userId]);
}

/** K-563 c: Kontomenü „Wie das Gerät“: zurück zu Automatisch, ohne ins Profil zu müssen. */
export function autoThemeForAccount(): void {
  const userId = api.session()?.userId;
  setThemePreference("system");
  if (userId) void saveThemePreference(userId, "system").catch(() => undefined);
}

/** Kontomenü „Hell- oder Dunkelmodus“: wechselt sofort und speichert die feste Wahl im Konto (Fehler: bleibt im Browser). */
export function toggleThemeForAccount(): void {
  const userId = api.session()?.userId;
  const next = toggleTheme();
  if (userId) void saveThemePreference(userId, next).catch(() => undefined);
}

function options(t: (de: string, en: string) => string): { value: ThemePreference; title: string; text: string }[] {
  return [
    { value: "light", title: t("Hell", "Light"), text: t("Helle Flächen, dunkle Schrift.", "Light surfaces, dark text.") },
    { value: "dark", title: t("Dunkel", "Dark"), text: t("Anthrazit wie die Website, helle Schrift.", "Charcoal like the website, light text.") },
    { value: "system", title: t("Automatisch", "Automatic"), text: t("Standard: folgt deinem Gerät, hell oder dunkel.", "Default: follows your device, light or dark.") },
  ];
}

/** Kleine Vorschau der Ansicht (Rahmen, Seite, Karte, Akzent); rein dekorativ. */
function Preview({ art }: { art: "light" | "dark" }) {
  return <span className="tk-darstellung-vorschau" data-art={art} aria-hidden="true"><i /></span>;
}

/**
 * Die drei Karten Hell, Dunkel, Automatisch mit kleiner Vorschau als Auswahlgruppe (Pfeiltasten wechseln, Leertaste wählt).
 * liste: Karten als Zeilen für schmale Spalten (Einladung); am Handy immer als Liste (buehne.css).
 */
export function DarstellungKarten({ value, onChange, t = uiT, labelledBy, describedBy, busy, liste }: {
  value: ThemePreference;
  onChange: (next: ThemePreference) => void;
  t?: (de: string, en: string) => string;
  labelledBy: string;
  describedBy?: string;
  busy?: boolean;
  liste?: boolean;
}) {
  const name = useId();
  return (
    <div className={liste ? "tk-darstellung tk-darstellung-liste" : "tk-darstellung"} role="radiogroup" aria-labelledby={labelledBy} aria-describedby={describedBy} aria-busy={busy}>
      {options(t).map((o) => (
        <label key={o.value} className="tk-darstellung-karte">
          <input type="radio" name={name} value={o.value} checked={value === o.value} onChange={() => onChange(o.value)} />
          {o.value === "system"
            ? <span className="tk-darstellung-paar"><Preview art="light" /><Preview art="dark" /></span>
            : <Preview art={o.value} />}
          <strong>{o.title}</strong>
          <small>{o.text}</small>
        </label>
      ))}
    </div>
  );
}

/**
 * Wahl dieser Seite (data-theme-wahl am <html>) als React-Zustand: die Karten zeigen immer, was die Seite zeigt, auch nach dem
 * Schalter im Kopf oder im Kontomenü und nachdem der Rahmen die Wahl aus dem Konto übernommen hat.
 */
function useSeitenWahl(): ThemePreference {
  return useSyncExternalStore(
    (onChange) => {
      if (typeof document === "undefined" || typeof MutationObserver === "undefined") return () => {};
      const o = new MutationObserver(onChange);
      o.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme-wahl"] });
      return () => o.disconnect();
    },
    currentThemePreference,
    currentThemePreference,
  );
}

/** Abschnitt „Darstellung“ im Profil: drei Karten zur Wahl, wirkt sofort, gilt für die Person auf jedem Gerät. */
export function DarstellungWahl({ userId, t = uiT, heading = "h2" }: {
  userId: string;
  t?: (de: string, en: string) => string;
  heading?: "h2" | "h3";
}) {
  const id = useId();
  // Nach der Anmeldung übernimmt der Rahmen die gewählte Darstellung aus dem Konto (useThemeFromAccount); die Seite zeigt sie
  const current = useSeitenWahl();
  const [busy, setBusy] = useState(false);
  const H = heading;

  async function choose(next: ThemePreference) {
    if (next === currentThemePreference()) return;
    setThemePreference(next);
    setBusy(true);
    try {
      // Rückmeldung ist die Ansicht selbst (wechselt sofort); ein Hinweis nur, wenn das Konto die Wahl nicht speichern kann
      await saveThemePreference(userId, next);
    } catch {
      toast(themeNotSavedText(t), "alert");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel stack" aria-labelledby={`${id}-titel`}>
      <H id={`${id}-titel`}>{t("Darstellung", "Appearance")}</H>
      <DarstellungKarten value={current} onChange={(v) => void choose(v)} t={t} labelledBy={`${id}-titel`} describedBy={`${id}-hinweis`} busy={busy} />
      <small id={`${id}-hinweis`}>{t("Gilt für dich in beiden Portalen und auf jedem Gerät, sobald du angemeldet bist. Rechnungen und Verträge bleiben weiß wie Papier.",
        "Applies to you in both portals and on every device once you are signed in. Invoices and contracts stay white like paper.")}</small>
    </section>
  );
}

/**
 * Auswahl für einen Schritt der Anfangs-Einrichtung: die Wahl dieser Seite, also die des Browsers, ohne Wahl Dunkel; hat die
 * Person schon gewählt, zeigt die Seite ihre Wahl aus dem Konto (auch außerhalb des Portalrahmens, etwa auf der Einladung).
 * Jede Änderung wirkt sofort und wird im Browser gemerkt (auch über die Bestätigungsmail hinweg); ins Konto schreibt der
 * Schritt mit saveThemeChoice.
 */
export function useDarstellungEntwurf(userId: string | null): [ThemePreference, (next: ThemePreference) => void] {
  const value = useSeitenWahl();
  const touched = useRef(false);
  useEffect(() => {
    if (!userId || themeFromAddress()) return;
    let active = true;
    void loadThemeState(userId).then(({ preference }) => {
      if (active && !touched.current && preference && preference !== currentThemePreference()) setThemePreference(preference);
    });
    return () => {
      active = false;
    };
  }, [userId]);
  const change = (next: ThemePreference) => {
    touched.current = true;
    setThemePreference(next);
  };
  return [value, change];
}

/**
 * Abschnitt „Wie soll dein Portal aussehen?“ im ersten Schritt der Anfangs-Einrichtung (Agentur: Konto anlegen; Creator und
 * Teammitglieder: Einladung annehmen). Dunkel ist vorausgewählt, jede Wahl wirkt sofort; gespeichert wird mit dem Schritt.
 */
export function DarstellungAbschnitt({ value, onChange, t = uiT, heading = "h3", liste }: {
  value: ThemePreference;
  onChange: (next: ThemePreference) => void;
  t?: (de: string, en: string) => string;
  heading?: "h2" | "h3";
  liste?: boolean;
}) {
  const id = useId();
  const H = heading;
  return (
    <section className="stack tk-darstellung-frage" aria-labelledby={`${id}-titel`}>
      <H id={`${id}-titel`}>{t("Wie soll dein Portal aussehen?", "How should your portal look?")}</H>
      <DarstellungKarten value={value} onChange={onChange} t={t} labelledBy={`${id}-titel`} describedBy={`${id}-hinweis`} liste={liste} />
      <small id={`${id}-hinweis`}>{t("Die Auswahl wirkt sofort. Ändern kannst du sie jederzeit im Profil; Rechnungen und Verträge bleiben weiß wie Papier.",
        "Your choice applies right away. You can change it any time in your profile; invoices and contracts stay white like paper.")}</small>
    </section>
  );
}
